/**
 * Shrine Pro â€” Clean Replacement main.js
 *
 * This file replaces the original obfuscated main.js.
 * Written in plain vanilla JavaScript. No dependencies. No external calls.
 *
 * Shopify AJAX endpoints used (internal only):
 *   /cart/add.js
 *   /cart/change.js
 *   /cart/update.js
 *   /cart.js
 *   /recommendations/products.json (Shopify native)
 *   window.routes.predictive_search_url (Shopify native, set in theme.liquid)
 *
 * All custom elements check for DOM existence before running.
 * Missing sections or markup will not throw errors.
 */

'use strict';

/* ============================================================
   SECTION 1 â€” UTILITIES
   ============================================================ */

/**
 * Debounce: delays fn execution until after `wait` ms of inactivity.
 */
function debounce(fn, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Safe querySelector â€” returns null instead of throwing if selector is invalid.
 */
function qs(selector, parent) {
  try {
    return (parent || document).querySelector(selector);
  } catch (e) {
    return null;
  }
}

/**
 * Safe querySelectorAll â€” returns empty NodeList if selector is invalid.
 */
function qsa(selector, parent) {
  try {
    return (parent || document).querySelectorAll(selector);
  } catch (e) {
    return document.querySelectorAll('___no_match___');
  }
}

/**
 * Trap keyboard focus inside an element (accessibility).
 */
function trapFocus(el, focusEl) {
  if (!el) return;
  const focusable = el.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  function handleTab(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  el.addEventListener('keydown', handleTab);
  el._trapFocusHandler = handleTab;
  (focusEl || first).focus();
}

function removeTrapFocus(el) {
  if (!el || !el._trapFocusHandler) return;
  el.removeEventListener('keydown', el._trapFocusHandler);
  delete el._trapFocusHandler;
}

/**
 * Pause all media (video/audio) inside an element.
 */
function pauseAllMedia() {
  qsa('video').forEach(v => { try { v.pause(); } catch(e){} });
  qsa('.js-youtube').forEach(el => { el.contentWindow && el.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'); });
  qsa('.js-vimeo').forEach(el => { el.contentWindow && el.contentWindow.postMessage('{"method":"pause"}', '*'); });
}

/**
 * Fetch wrapper â€” only allows Shopify-internal paths or window.routes values.
 */
function safeFetch(url, options) {
  const allowedPrefixes = [
    '/cart', '/recommendations', '/search', '/collections', '/products',
    window.shopUrl || ''
  ];
  const isAllowed = allowedPrefixes.some(prefix => url.startsWith(prefix) || (prefix && url.startsWith(prefix)));
  if (!isAllowed) {
    console.warn('[main.js] Blocked external fetch to:', url);
    return Promise.reject(new Error('External fetch blocked'));
  }
  return fetch(url, options);
}

function cartAddJsonUrl() {
  const route = (window.routes && window.routes.cart_add_url) || '/cart/add';
  if (/\.js($|\?)/.test(route)) return route;
  return route.replace(/\/cart\/add($|\?)/, '/cart/add.js$1');
}

function cartChangeUrl() {
  const route = (window.routes && window.routes.cart_change_url) || '/cart/change';
  if (/\.js($|\?)/.test(route)) return route;
  return route.replace(/\/cart\/change($|\?)/, '/cart/change.js$1');
}

/* ============================================================
   FREE GIFT â€” auto-add a $0 gift product whenever the cart
   contains a paid item; remove it when the cart no longer
   qualifies. Keeps the gift at quantity 1.
   ============================================================ */

const FREE_GIFT = { variantId: 47251258704009, handle: 'ryng-v2-adjustable-hands-free-belt-gift' };
// products that earn the free belt. add a handle here when a new product should include it.
const FREE_GIFT_HANDLES = ['the-ryng-v2', 'the-ryng-v3', 'the-ryng-contour-v3', 'the-ryng-v4'];
let _giftSyncInFlight = false;

function injectGiftStyles() {
  if (document.getElementById('ryng-free-gift-style')) return;
  const s = document.createElement('style');
  s.id = 'ryng-free-gift-style';
  s.textContent =
    '.cart-item--product-' + FREE_GIFT.handle + ' .cart-item__name::after{' +
    'content:"FREE BELT";display:inline-block;margin-left:.6rem;background:#5a3a2c;color:#fff;' +
    'font-size:1rem;font-weight:700;letter-spacing:.02em;padding:.2rem .6rem;border-radius:.5rem;' +
    'vertical-align:middle;line-height:1.4;}';
  document.head.appendChild(s);
}

function _applyGiftDrawer(sections, itemCount) {
  const cd = qs('cart-drawer');
  if (cd && sections && sections['cart-drawer']) {
    const fresh = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('cart-drawer');
    if (fresh) {
      cd.innerHTML = fresh.innerHTML;
      if (itemCount != null) cd.classList.toggle('is-empty', itemCount === 0);
    }
  }
  if (itemCount == null) return;
  qsa('.cart-count-bubble').forEach(el => {
    const inner = qs('[aria-hidden]', el);
    if (itemCount > 0) { el.removeAttribute('hidden'); if (inner) inner.textContent = itemCount; }
    else { el.setAttribute('hidden', ''); }
  });
}

function syncFreeGift() {
  if (_giftSyncInFlight) return Promise.resolve();
  _giftSyncInFlight = true;
  const opts = body => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });
  return fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
      const gift = cart.items.find(i => i.handle === FREE_GIFT.handle);
      // one free belt per qualifying UNIT, not per order. 2 rings -> 2 belts.
      const giftQty = cart.items.reduce(function (n, i) {
        return FREE_GIFT_HANDLES.indexOf(i.handle) !== -1 && i.final_price > 0 ? n + i.quantity : n;
      }, 0);
      const sectionMeta = { sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname };

      if (giftQty > 0 && !gift) {
        return safeFetch(cartAddJsonUrl(), opts(Object.assign({ id: FREE_GIFT.variantId, quantity: giftQty }, sectionMeta)))
          .then(r => r.json())
          .then(res => fetch('/cart.js').then(r => r.json()).then(c => _applyGiftDrawer(res.sections, c.item_count)));
      }
      // covers scale up, scale down, and removal when giftQty is 0
      if (gift && gift.quantity !== giftQty) {
        return safeFetch(cartChangeUrl(), opts(Object.assign({ id: gift.key, quantity: giftQty }, sectionMeta)))
          .then(r => r.json())
          .then(c => _applyGiftDrawer(c.sections, c.item_count));
      }
      return null;
    })
    .catch(() => {})
    .then(v => { _giftSyncInFlight = false; return v; });
}

if (document.readyState !== 'loading') injectGiftStyles();
else document.addEventListener('DOMContentLoaded', injectGiftStyles);

/* ============================================================
   SECTION 2 â€” CUSTOM ELEMENT: QuantityInput
   ============================================================ */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = qs('input[type="number"]', this);
    this.decrementBtn = qs('[name="minus"]', this);
    this.incrementBtn = qs('[name="plus"]', this);
    if (!this.input) return;
    this.decrementBtn && this.decrementBtn.addEventListener('click', () => this._change(-1));
    this.incrementBtn && this.incrementBtn.addEventListener('click', () => this._change(1));
  }

  _change(delta) {
    const current = parseInt(this.input.value, 10) || 1;
    const min = parseInt(this.input.getAttribute('min'), 10) || 1;
    const max = parseInt(this.input.getAttribute('max'), 10) || Infinity;
    const next = Math.min(Math.max(current + delta, min), max);
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
customElements.define('quantity-input', QuantityInput);

/* ============================================================
   SECTION 3 â€” CUSTOM ELEMENT: ProductForm
   ============================================================ */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = qs('form[data-type="add-to-cart-form"]', this);
    if (!this.form) return;
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.cartNotification = qs('cart-notification') || qs('[id^="cart-notification"]');
  }

  handleSubmit(e) {
    e.preventDefault();
    const submitBtn = qs('[type="submit"]', this.form);
    const variantId = qs('[name="id"]', this.form);
    if (!variantId || !variantId.value) return;

    if (submitBtn) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('loading');
    }

    const quantity = parseInt((qs('[name="quantity"]', this.form) || {}).value, 10) || 1;
    const items = [{ id: variantId.value, quantity }];
    const sectionId = this.dataset.section;
    const upsellSelector = sectionId
      ? `.product-info-upsell-${CSS.escape(sectionId)}[data-selected="true"]`
      : 'product-info-upsell[data-selected="true"]';

    qsa(upsellSelector).forEach(upsell => {
      if (!upsell.dataset.id) return;
      const existing = items.find(item => String(item.id) === String(upsell.dataset.id));
      if (existing) existing.quantity += 1;
      else items.push({ id: upsell.dataset.id, quantity: 1 });
    });

    const body = JSON.stringify({
      items,
      sections: this._getSectionsToRender().map(s => s.section),
      sections_url: window.location.pathname,
    });

    safeFetch(cartAddJsonUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    })
      .then(async r => JSON.parse(await r.text()))
      .then(async response => {
        if (response.status) {
          this._handleError(response, submitBtn);
          return;
        }
        this._updateSections(response);
        if (this._isUpsellForm()) this._confirmUpsellAdded(submitBtn);
        else this._openCartDrawer();
        syncFreeGift();
        const cart = response.item_count != null ? response : await safeFetch('/cart.js').then(r => r.json());
        this._updateCartBubble(cart.item_count);
      })
      .catch(err => {
        console.warn('[ProductForm] cart/add.js warning:', err);
        this._handleError({ description: window.cartStrings && window.cartStrings.error }, submitBtn);
      })
      .finally(() => {
        if (submitBtn) {
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('loading');
        }
      });
  }

  _getSectionsToRender() {
    // Sections to re-render after ATC. Override if cart-drawer is present.
    const sections = [];
    if (qs('cart-drawer')) sections.push({ section: 'cart-drawer', selector: 'cart-drawer' });
    if (qs('[id^="shopify-section-cart-icon-bubble"]')) {
      sections.push({ section: 'cart-icon-bubble', selector: '.shopify-section-cart-icon-bubble' });
    }
    return sections;
  }

  _updateSections(response) {
    const sections = response.sections || {};
    Object.keys(sections).forEach(key => {
      const html = sections[key];
      if (!html) return;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const cartDrawer = qs('cart-drawer');
      if (cartDrawer && key.includes('cart-drawer')) {
        const newDrawer = doc.querySelector('cart-drawer');
        if (newDrawer) cartDrawer.innerHTML = newDrawer.innerHTML;
      }
    });
  }

  _openCartDrawer() {
    const cartDrawer = qs('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  }

  /* Adding an UPSELL must never take the customer off the product page.
     The cart drawer is full-screen on mobile (cart-drawer--mobile-width-full
     + body overflow hidden), so opening it reads as a page change and the
     customer has to back out before they can buy the main product.
     Upsell forms confirm in place; only the main product form opens the
     drawer. Applies to every PDP, since this file is shared. 2026-09-10. */
  _isUpsellForm() {
    return !!(this.closest('.upsell') || this.dataset.isCartUpsell === 'true');
  }

  _confirmUpsellAdded(submitBtn) {
    const card = this.closest('.upsell');
    const btn = (card && qs('.upsell__add-btn', card)) || submitBtn;
    if (!btn) return;
    const label = qs('span', btn) || btn;
    if (btn.dataset.ryngAddedTimer) clearTimeout(Number(btn.dataset.ryngAddedTimer));
    else btn.dataset.ryngLabel = label.textContent;
    label.textContent = 'added to cart';
    btn.classList.add('upsell__add-btn--added');
    const timer = setTimeout(() => {
      if (btn.dataset.ryngLabel) label.textContent = btn.dataset.ryngLabel;
      btn.classList.remove('upsell__add-btn--added');
      delete btn.dataset.ryngAddedTimer;
    }, 2200);
    btn.dataset.ryngAddedTimer = String(timer);
  }

  _updateCartBubble(count) {
    qsa('.cart-count-bubble').forEach(el => {
      if (count > 0) {
        el.removeAttribute('hidden');
        const countEl = qs('[aria-hidden]', el);
        if (countEl) countEl.textContent = count;
      } else {
        el.setAttribute('hidden', '');
      }
    });
    /* The ryng header renders its own counter, not Dawn's .cart-count-bubble.
       Desktop prints "(N)", mobile prints "N". While the drawer opened on
       every add this went unnoticed; now that an upsell add stays on the PDP
       the header count is the only lasting confirmation, so it must update. */
    qsa('.ryng-cart-count').forEach(el => {
      el.textContent = el.textContent.trim().startsWith('(') ? '(' + count + ')' : String(count);
    });
  }

  _handleError(error, submitBtn) {
    const wrapper = qs('.product-form__error-message-wrapper', this);
    const msg = qs('.product-form__error-message', this);
    if (wrapper && msg) {
      wrapper.removeAttribute('hidden');
      msg.textContent = error.description || (window.cartStrings && window.cartStrings.error) || 'Error adding to cart.';
    }
  }
}
customElements.define('product-form', ProductForm);

class ProductInfoUpsell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ryngInitialized === 'true') return;
    this.dataset.ryngInitialized = 'true';
    this.toggleButton = qs('.upsell-toggle-btn', this);
    this.variantSelects = qs('.upsell__variant-picker', this);
    this.variantSelectElements = Array.from(qsa('select', this));
    this.variantDataElement = qs('script[type="application/json"]', this);
    this.idInput = qs('form[action*="/cart/add"] [name="id"]', this);
    this.excludedVariantIds = new Set(
      (this.dataset.excludedVariantIds || '')
        .split(',')
        .map(value => value.trim())
        .filter(Boolean)
    );

    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', event => this.toggleSelection(event));
    }
    if (this.variantSelects) {
      this.variantSelects.addEventListener('change', () => this.syncVariant());
    }

    this.filterExcludedOptions();
    this.syncVariant();
  }

  getVariantData() {
    if (this.variantData) return this.variantData;
    if (!this.variantDataElement) return [];
    try {
      this.variantData = JSON.parse(this.variantDataElement.textContent);
    } catch (error) {
      console.warn('[ProductInfoUpsell] Invalid variant data:', error);
      this.variantData = [];
    }
    return this.variantData;
  }

  allowedVariants() {
    return this.getVariantData().filter(variant => !this.excludedVariantIds.has(String(variant.id)));
  }

  filterExcludedOptions() {
    if (!this.excludedVariantIds.size) return;
    const allowed = this.allowedVariants();
    this.variantSelectElements.forEach((select, optionIndex) => {
      Array.from(select.options).forEach(option => {
        const isAllowed = allowed.some(variant => variant.options[optionIndex] === option.value);
        if (!isAllowed) option.remove();
      });
    });
  }

  toggleSelection(event) {
    if (event.target.closest('select, option')) return;
    event.preventDefault();
    this.dataset.selected = this.dataset.selected === 'true' ? 'false' : 'true';
  }

  syncVariant() {
    const selectedOptions = this.variantSelectElements.map(select => select.value);
    const allowed = this.allowedVariants();
    let variant = allowed.find(item => item.options.every((option, index) => option === selectedOptions[index]));
    if (!variant) variant = allowed.find(item => item.available) || allowed[0];
    if (!variant) return;

    this.variantSelectElements.forEach((select, index) => {
      if (variant.options[index] != null) select.value = variant.options[index];
    });
    this.dataset.id = String(variant.id);
    if (this.idInput) this.idInput.value = String(variant.id);

    const image = qs('.upsell__image__img', this);
    const variantImage = variant.featured_image;
    if (image && variantImage && variantImage.src) {
      const separator = variantImage.src.includes('?') ? '&' : '?';
      image.src = `${variantImage.src}${separator}width=500`;
      if (variantImage.alt) image.alt = variantImage.alt;
    }
  }
}

if (!customElements.get('product-info-upsell')) {
  customElements.define('product-info-upsell', ProductInfoUpsell);
}

/* ============================================================
   SECTION 4 â€” CUSTOM ELEMENT: VariantSelects
   ============================================================ */

class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '');
    this.updatePickupAvailability();
    if (!this.currentVariant) {
      this.toggleAddButton(true, '');
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select, fieldset')).map(el => {
      if (el.tagName === 'SELECT') return el.value;
      const checked = el.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : '';
    });
  }

  updateMasterId() {
    const productDataEl = qs('[id^="ProductJSON-"]');
    if (!productDataEl) { this.currentVariant = null; return; }
    try {
      const product = JSON.parse(productDataEl.textContent);
      this.currentVariant = product.variants.find(v =>
        v.options.every((opt, i) => opt === this.options[i])
      ) || null;
    } catch (e) {
      this.currentVariant = null;
    }
  }

  updateMedia() {
    if (!this.currentVariant) return;

    const rawMediaId =
      (this.currentVariant.featured_media && this.currentVariant.featured_media.id) ||
      (this.currentVariant.featured_image && this.currentVariant.featured_image.id) ||
      this.currentVariant.image_id;

    if (!rawMediaId) return;

    const sectionId = this.dataset.section;
    const mediaId = sectionId ? `${sectionId}-${rawMediaId}` : String(rawMediaId);
    const gallery =
      (sectionId && document.getElementById(`MediaGallery-${sectionId}`)) ||
      (this.closest('.product') && this.closest('.product').querySelector('media-gallery')) ||
      qs('media-gallery');

    if (gallery && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(mediaId, true, this.dataset.hasFiltering === 'true', this.currentVariant);
      return;
    }

    const mediaEl =
      qs(`[data-media-id="${mediaId}"]`) ||
      qs(`[data-media-id$="-${rawMediaId}"]`) ||
      qs(`[data-media-id="${rawMediaId}"]`);

    if (!mediaEl) return;

    const slide = mediaEl.closest('.product__media-item, .slider__slide') || mediaEl;
    const slider = slide.parentElement;
    if (slider && typeof slider.scrollTo === 'function') {
      slider.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    } else {
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({}, '', `${window.location.pathname}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const input = qs('.product-variant-id');
    if (input && this.currentVariant) input.value = this.currentVariant.id;
  }

  updateShareUrl() {
    const shareBtn = qs('share-button');
    if (!shareBtn || !this.currentVariant) return;
    const url = qs('[data-share-url]', shareBtn);
    if (url) url.value = `${window.location.origin}${window.location.pathname}?variant=${this.currentVariant.id}`;
  }

  renderProductInfo() {
    if (!this.currentVariant) return;
    const sectionId = this.dataset.section;

    // Update price
    const priceEl = qs(`#price-${sectionId}`);
    if (priceEl) {
      const regular = priceEl.querySelector('.price__regular');
      const sale = priceEl.querySelector('.price__sale');
      const regularAmount = priceEl.querySelector('.price-item--regular');
      const saleAmount = priceEl.querySelector('.price-item--sale');
      const compareAmount = priceEl.querySelector('.price-item--compare');
      const v = this.currentVariant;

      if (v.compare_at_price > v.price) {
        if (regular) regular.classList.add('hidden');
        if (sale) sale.classList.remove('hidden');
        if (saleAmount) saleAmount.textContent = this._formatMoney(v.price);
        if (compareAmount) compareAmount.textContent = this._formatMoney(v.compare_at_price);
      } else {
        if (regular) regular.classList.remove('hidden');
        if (sale) sale.classList.add('hidden');
        if (regularAmount) regularAmount.textContent = this._formatMoney(v.price);
      }
    }

    // Update SKU
    const skuEl = qs(`#Sku-${sectionId}`);
    if (skuEl) {
      const value = skuEl.querySelector('.variant-sku');
      if (value) value.textContent = this.currentVariant.sku || '';
    }

    // Update inventory
    const invEl = qs(`#Inventory-${sectionId}`);
    if (invEl) {
      invEl.classList.toggle('visibility-hidden',
        this.currentVariant.inventory_management !== 'shopify');
    }

    this.toggleAddButton(
      !this.currentVariant.available,
      this.currentVariant.available
        ? (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart'
        : (window.variantStrings && window.variantStrings.soldOut) || 'Sold out'
    );
  }

  _formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  toggleAddButton(disable, text) {
    const btn = qs('[id^="ProductSubmitButton"]');
    if (!btn) return;
    if (!disable && btn.dataset.availableLabel) text = btn.dataset.availableLabel;
    if (disable) {
      btn.setAttribute('disabled', 'disabled');
      if (text) {
        const label = btn.querySelector('.button__label') || btn;
        label.textContent = text;
      }
    } else {
      btn.removeAttribute('disabled');
      if (text) {
        const label = btn.querySelector('.button__label') || btn;
        label.textContent = text;
      }
    }
  }

  setUnavailable() {
    const btn = qs('[id^="ProductSubmitButton"]');
    if (!btn) return;
    btn.setAttribute('disabled', 'disabled');
    const label = btn.querySelector('.button__label') || btn;
    label.textContent = (window.variantStrings && window.variantStrings.unavailable) || 'Unavailable';
  }

  updatePickupAvailability() {
    const pickup = qs('pickup-availability');
    if (!pickup) return;
    if (this.currentVariant && this.currentVariant.available) {
      pickup.fetchAvailability(this.currentVariant.id);
    } else {
      pickup.removeAttribute('available');
      qs('pickup-availability-preview', pickup) && (qs('pickup-availability-preview', pickup).innerHTML = '');
    }
  }
}
customElements.define('variant-selects', VariantSelects);

class CardVariantSelects extends VariantSelects {}
customElements.define('card-variant-selects', CardVariantSelects);

/* ============================================================
   SECTION 5 â€” CUSTOM ELEMENT: ModalDialog
   ============================================================ */

class ModalDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = qs('[role="dialog"]', this) || qs('details', this);
    this.closeBtn = qs('[id^="ModalClose"]', this) || qs('[data-close-modal]', this);
    this.closeBtn && this.closeBtn.addEventListener('click', () => this.hide());
    this.addEventListener('keydown', e => { if (e.key === 'Escape') this.hide(); });
  }

  show(opener) {
    this._opener = opener;
    this.removeAttribute('hidden');
    this.setAttribute('open', '');
    this.querySelector('[tabindex="-1"]') && this.querySelector('[tabindex="-1"]').removeAttribute('tabindex');
    trapFocus(this);
    document.body.classList.add('overflow-hidden');
  }

  hide() {
    this.setAttribute('hidden', '');
    this.removeAttribute('open');
    removeTrapFocus(this);
    document.body.classList.remove('overflow-hidden');
    if (this._opener) { this._opener.focus(); this._opener = null; }
    pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

/* ============================================================
   SECTION 6 â€” CUSTOM ELEMENT: ModalOpener
   ============================================================ */

class ModalOpener extends HTMLElement {
  connectedCallback() {
    const btn = qs('button', this);
    if (!btn) return;
    btn.addEventListener('click', () => {
      const targetId = this.dataset.modal;
      const modal = targetId ? qs(`#${targetId}`) : null;
      if (modal && typeof modal.show === 'function') modal.show(btn);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

/* ============================================================
   SECTION 7 â€” CUSTOM ELEMENT: DetailsModal (accordion/tabs)
   ============================================================ */

class DetailsModal extends HTMLElement {
  connectedCallback() {
    this.detailsEl = qs('details', this);
    if (!this.detailsEl) return;
    this.summary = qs('summary', this.detailsEl);
    this.content = this.detailsEl.querySelector('[id^="Details-Content"]');
    this.summary && this.summary.addEventListener('click', e => this._toggle(e));
    this.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.detailsEl.hasAttribute('open')) {
        this.close();
      }
    });
  }

  _toggle(e) {
    if (this.detailsEl.hasAttribute('open')) {
      e.preventDefault();
      this.detailsEl.removeAttribute('open');
    }
  }

  open(opener) {
    this.detailsEl = this.detailsEl || qs('details', this);
    this.summary = this.summary || (this.detailsEl && qs('summary', this.detailsEl));
    this.openedBy = opener || this.summary;
    if (this.detailsEl) this.detailsEl.setAttribute('open', '');
  }

  close(focusToggle = true) {
    this.detailsEl = this.detailsEl || qs('details', this);
    this.summary = this.summary || (this.detailsEl && qs('summary', this.detailsEl));
    if (this.detailsEl) this.detailsEl.removeAttribute('open');
    if (focusToggle && this.summary) this.summary.focus();
  }
}
customElements.define('details-modal', DetailsModal);

/* ============================================================
   SECTION 8 â€” CUSTOM ELEMENT: CartDrawer
   ============================================================ */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
    /* Click-outside-to-close is DELEGATED to this host element, never bound
       to the overlay node itself. Every cart mutation re-renders this
       element's innerHTML (ProductForm._updateSections and
       CartItems._renderDrawer both do `cartDrawer.innerHTML = ...`), which
       destroys the overlay and any listener attached to it. The result was
       that clicking the dimmed page closed the drawer on a fresh load but
       silently stopped working after the first add-to-cart - the exact
       moment the drawer is most likely to be open. The X kept working only
       because it carries an inline onclick in the markup.
       The overlay is a SIBLING of .drawer__inner, so this cannot swallow
       clicks inside the panel. 2026-09-10. */
    this.addEventListener('click', event => {
      if (!this.classList.contains('active')) return;
      const target = event.target;
      if (target.closest('.cart-drawer__overlay') || target.closest('[data-close-drawer]')) {
        event.preventDefault();
        this.close();
      }
    });
    qsa('[aria-controls="CartDrawer"]').forEach(el => el.addEventListener('click', event => {
      event.preventDefault();
      this.open();
    }));
  }

  open() {
    this.setAttribute('aria-expanded', 'true');
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    trapFocus(this);
    this.refreshCart();
    syncFreeGift();
  }

  close() {
    this.setAttribute('aria-expanded', 'false');
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this);
  }

  refreshCart() {
    safeFetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        this.classList.toggle('is-empty', cart.item_count === 0);
        const countEls = qsa('.cart-count-bubble');
        countEls.forEach(el => {
          const inner = qs('[aria-hidden]', el);
          if (cart.item_count > 0) {
            el.removeAttribute('hidden');
            if (inner) inner.textContent = cart.item_count;
          } else {
            el.setAttribute('hidden', '');
          }
        });
      })
      .catch(() => {});
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ============================================================
   SECTION 9 â€” CUSTOM ELEMENT: CartItems
   ============================================================ */

class CartItems extends HTMLElement {
  connectedCallback() {
    this.debouncedChange = debounce(this._onChange.bind(this), 300);
    this.addEventListener('change', this.debouncedChange);
  }

  _onChange(e) {
    const input = e.target.closest('input[name="updates[]"], .quantity__input');
    if (!input) return;
    const line = parseInt(input.dataset.index, 10);
    const qty = parseInt(input.value, 10);
    if (isNaN(line) || isNaN(qty)) return;
    this.updateQuantity(line, qty);
  }

  updateQuantity(line, quantity) {
    this._setLoading(line, true);
    // Decide what to re-render based on WHICH element this is:
    // the drawer (<cart-drawer-items>) re-renders the drawer; the /cart page
    // (<cart-items>) re-renders the cart page sections. Both <cart-drawer> and
    // the page can coexist in the DOM, so branch on tag, not on presence.
    const isDrawer = this.tagName.toLowerCase() === 'cart-drawer-items';
    const itemsEl = document.getElementById('main-cart-items');
    const footerEl = document.getElementById('main-cart-footer');
    const pageItemsId = itemsEl ? itemsEl.dataset.id : null;
    const pageFooterId = footerEl ? footerEl.dataset.id : null;
    const sections = isDrawer
      ? ['cart-drawer', 'cart-icon-bubble']
      : [pageItemsId, pageFooterId].filter(Boolean);

    const body = { line, quantity, sections_url: window.location.pathname };
    if (sections.length) body.sections = sections;

    safeFetch(cartChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    })
      .then(r => r.json())
      .then(cart => {
        if (cart.status) { this._setLoading(line, false); return; }
        if (isDrawer) {
          if (cart.sections && cart.sections['cart-drawer']) this._renderDrawer(cart);
          syncFreeGift();
        } else {
          this._renderPage(cart, pageItemsId, pageFooterId);
        }
      })
      .catch(() => this._setLoading(line, false));
  }

  _renderPage(cart, itemsId, footerId) {
    if (cart.item_count === 0) { window.location.reload(); return; }
    [['main-cart-items', itemsId], ['main-cart-footer', footerId]].forEach(([elId, secId]) => {
      if (!secId || !cart.sections || !cart.sections[secId]) return;
      const parsed = new DOMParser().parseFromString(cart.sections[secId], 'text/html');
      const src = parsed.querySelector('.js-contents');
      const host = document.getElementById(elId);
      const dest = host && host.querySelector('.js-contents');
      if (src && dest) dest.innerHTML = src.innerHTML;
    });
    this._updateBubble(cart.item_count);
  }

  _renderDrawer(cart) {
    const cartDrawer = qs('cart-drawer');
    const html = cart.sections['cart-drawer'];
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
    if (cartDrawer && fresh) {
      cartDrawer.innerHTML = fresh.innerHTML;
      cartDrawer.classList.toggle('is-empty', cart.item_count === 0);
    }
    this._updateBubble(cart.item_count);
  }

  _updateBubble(count) {
    qsa('.cart-count-bubble').forEach(el => {
      const inner = qs('[aria-hidden]', el);
      if (count > 0) {
        el.removeAttribute('hidden');
        if (inner) inner.textContent = count;
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  _setLoading(line, on) {
    const item = this.querySelector(`[id$="-Item-${line}"]`) || this.querySelector(`[data-index="${line}"]`);
    const overlay = item && qs('.loading-overlay', item);
    if (overlay) overlay.classList.toggle('hidden', !on);
  }
}
customElements.define('cart-items', CartItems);

// Cart drawer uses <cart-drawer-items>; reuse the same logic via a subclass.
class CartDrawerItems extends CartItems {}
customElements.define('cart-drawer-items', CartDrawerItems);

class CartRemoveButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', e => {
      e.preventDefault();
      const line = parseInt(this.dataset.index, 10);
      if (isNaN(line)) return;
      const container = this.closest('cart-drawer-items, cart-items');
      if (container && typeof container.updateQuantity === 'function') {
        container.updateQuantity(line, 0);
        return;
      }
      safeFetch(cartChangeUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ line, quantity: 0, sections_url: window.location.pathname }),
      })
        .then(r => r.json())
        .then(() => { window.location.reload(); })
        .catch(() => {});
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

/* ============================================================
   SECTION 10 â€” CUSTOM ELEMENT: HeaderDrawer (mobile nav)
   ============================================================ */

class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.mainDetailsToggle = qs('details', this);
    this._summaryClickHandler = this._summaryClickHandler || this._onSummaryClick.bind(this);
    this._keyUpHandler = this._keyUpHandler || this._onKeyUp.bind(this);
    this._focusOutHandler = this._focusOutHandler || this._onFocusOut.bind(this);

    qsa('summary', this).forEach(s => {
      s.setAttribute('role', 'button');
      s.addEventListener('click', this._summaryClickHandler);
      s.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const details = s.closest('details');
          if (details === this.mainDetailsToggle) {
            details.hasAttribute('open') ? this.closeMenuDrawer(e, s) : this.openMenuDrawer(s);
          } else if (details) {
            details.hasAttribute('open') ? this.closeSubmenu(details) : this.openSubmenu(details, s);
          }
        }
      });
    });

    qsa('.menu-drawer__close-btn', this).forEach(btn => {
      btn.addEventListener('click', e => this.closeMenuDrawer(e, this.mainDetailsToggle && this.mainDetailsToggle.querySelector('summary')));
    });
    qsa('button:not(.menu-drawer__close-menu-btn)', this).forEach(btn => {
      btn.addEventListener('click', e => this.closeSubmenu(e.currentTarget.closest('details')));
    });
    this.addEventListener('keyup', this._keyUpHandler);
    this.addEventListener('focusout', this._focusOutHandler);
  }

  _onSummaryClick(e) {
    const summary = e.currentTarget;
    const details = summary.closest('details');
    if (!details) return;
    const isOpen = details.hasAttribute('open');

    if (details === this.mainDetailsToggle) {
      e.preventDefault();
      isOpen ? this.closeMenuDrawer(e, summary) : this.openMenuDrawer(summary);
      return;
    }

    if (isOpen) {
      e.preventDefault();
      this.closeSubmenu(details);
      return;
    }

    setTimeout(() => this.openSubmenu(details, summary), 100);
  }

  _onKeyUp(e) {
    if (e.code && e.code.toUpperCase() !== 'ESCAPE') return;
    const details = e.target.closest('details[open]');
    if (!details) return;

    if (details === this.mainDetailsToggle) {
      this.closeMenuDrawer(e, this.mainDetailsToggle.querySelector('summary'));
    } else {
      this.closeSubmenu(details);
    }
  }

  _onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle && this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) {
        this.closeMenuDrawer();
      }
    });
  }

  openSubmenu(details, summary) {
    if (!details || !summary) return;
    const parentMenu = details.closest('.has-submenu');
    details.classList.add('menu-opening');
    summary.setAttribute('aria-expanded', 'true');
    if (parentMenu) parentMenu.classList.add('submenu-open');

    const submenu = summary.nextElementSibling;
    if (!submenu) return;

    const focusTarget = details.querySelector('button');
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const focusSubmenu = () => {
      trapFocus(submenu, focusTarget);
      submenu.removeEventListener('transitionend', focusSubmenu);
    };

    if (prefersReducedMotion.matches) {
      focusSubmenu();
    } else {
      submenu.addEventListener('transitionend', focusSubmenu);
    }
  }

  openMenuDrawer(btn) {
    this._opener = btn;
    if (this.mainDetailsToggle) {
      this.mainDetailsToggle.setAttribute('open', '');
      requestAnimationFrame(() => {
        this.mainDetailsToggle.classList.add('menu-opening');
      });
    }
    if (btn) btn.setAttribute('aria-expanded', 'true');

    const header = document.querySelector('.section-header');
    if (header) {
      header.classList.add('menu-open');
      const headerWrapper = this.closest('.header-wrapper');
      const borderOffset = headerWrapper && headerWrapper.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
      document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(header.getBoundingClientRect().bottom - borderOffset, 10)}px`);
    }

    if (window.matchMedia('(max-width: 990px)').matches) {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }

    document.body.classList.add('overflow-hidden', `overflow-hidden-${this.dataset.breakpoint || 'tablet'}`);
    trapFocus(this.mainDetailsToggle || this, btn);
  }

  closeMenuDrawer(event, focusElement = false) {
    if (this.mainDetailsToggle) {
      this.mainDetailsToggle.classList.remove('menu-opening');
      this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
      this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
        submenu.classList.remove('submenu-open');
      });
      const summary = this.mainDetailsToggle.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
      this.closeAnimation(this.mainDetailsToggle);
    }

    const header = document.querySelector('.section-header');
    if (header) header.classList.remove('menu-open');

    document.body.classList.remove('overflow-hidden', `overflow-hidden-${this.dataset.breakpoint || 'tablet'}`);
    removeTrapFocus(focusElement || this._opener || false);
    if (this._opener) { this._opener.focus(); this._opener = null; }
  }

  closeSubmenu(details) {
    if (!details) return;
    const parentMenu = details.closest('.submenu-open');
    if (parentMenu) parentMenu.classList.remove('submenu-open');
    details.classList.remove('menu-opening');
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    removeTrapFocus(summary || false);
    this.closeAnimation(details);
  }

  closeAnimation(details) {
    if (!details) return;
    let animationStart;
    const handleAnimation = now => {
      if (animationStart === undefined) animationStart = now;
      if (now - animationStart < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        details.removeAttribute('open');
        const openParent = details.closest('details[open]');
        if (openParent) trapFocus(openParent, details.querySelector('summary'));
      }
    };
    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  connectedCallback() {
    super.connectedCallback();
    qsa('.menu-drawer__close-menu-btn', this).forEach(btn => {
      btn.addEventListener('click', e => {
        this.closeMenuDrawer(e, this.mainDetailsToggle && this.mainDetailsToggle.querySelector('summary'));
        const expandedToggle = qs('.header__icon--menu[aria-expanded="true"]', this);
        if (expandedToggle) expandedToggle.setAttribute('aria-expanded', 'false');
      });
    });
  }
}
customElements.define('header-drawer', HeaderDrawer);

/* ============================================================
   SECTION 13 â€” CUSTOM ELEMENT: DeferredMedia
   ============================================================ */

class DeferredMedia extends HTMLElement {
  connectedCallback() {
    const poster = qs('[id^="Deferred-Poster"]', this);
    poster && poster.addEventListener('click', () => this.loadContent());
  }

  loadContent(focus = true) {
    const template = qs('template', this);
    if (!template) return;
    const content = template.content.firstElementChild;
    if (!content) return;
    this.appendChild(content.cloneNode(true));
    template.remove();
    if (focus) {
      const media = qs('video, iframe', this);
      media && media.focus();
    }
  }
}
customElements.define('deferred-media', DeferredMedia);

/* ============================================================
   SECTION 14 â€” CUSTOM ELEMENT: ProductInfo
   ============================================================ */

class ProductInfo extends HTMLElement {
  connectedCallback() {
    // Wire up sticky ATC if present
    const stickyAtc = qs('sticky-atc', document);
    if (stickyAtc) {
      const atcBtn = qs('[id^="ProductSubmitButton"]', this);
      if (atcBtn) {
        const observer = new IntersectionObserver(([entry]) => {
          stickyAtc.classList.toggle('is-visible', !entry.isIntersecting);
        });
        observer.observe(atcBtn);
      }
    }
  }
}
customElements.define('product-info', ProductInfo);

/* ============================================================
   SECTION 15 â€” CUSTOM ELEMENT: StickyAtc
   ============================================================ */

class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.atcBtn = qs('[id^="SectionAtcBtn"]', this);
  }
}
customElements.define('sticky-atc', StickyAtc);

/* ============================================================
   SECTION 17 â€” CUSTOM ELEMENT: PredictiveSearch
   ============================================================ */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = qs('input[type="search"]', this);
    this.resultsList = qs('[id^="predictive-search-results"]', this);
    if (!this.input) return;

    this.input.addEventListener('input', debounce(this._onInput.bind(this), 300));
    this.input.addEventListener('focus', () => {
      if (this.input.value.length > 0) this._search(this.input.value);
    });
    document.addEventListener('click', e => {
      if (!this.contains(e.target)) this._close();
    });
    this.input.addEventListener('keydown', e => {
      if (e.key === 'Escape') this._close();
    });
  }

  _onInput(e) {
    const q = e.target.value.trim();
    if (q.length < 2) { this._close(); return; }
    this._search(q);
  }

  _search(q) {
    const url = `${window.routes && window.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,page&section_id=predictive-search`;
    safeFetch(url)
      .then(r => r.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const results = doc.querySelector('.predictive-search__results');
        if (this.resultsList && results) {
          this.resultsList.innerHTML = results.innerHTML;
          this.resultsList.removeAttribute('hidden');
        }
      })
      .catch(() => {});
  }

  _close() {
    this.resultsList && this.resultsList.setAttribute('hidden', '');
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ============================================================
   SECTION 18 â€” CUSTOM ELEMENT: ShareButton
   ============================================================ */

class ShareButton extends HTMLElement {
  connectedCallback() {
    this.shareBtn = qs('[data-share-button]', this) || qs('button', this);
    this.urlInput = qs('[data-share-url]', this);
    this.successMsg = qs('[data-success-message]', this);
    if (!this.shareBtn) return;

    this.shareBtn.addEventListener('click', () => {
      const url = (this.urlInput && this.urlInput.value) || window.location.href;
      if (navigator.share) {
        navigator.share({ url }).catch(() => {});
      } else {
        navigator.clipboard && navigator.clipboard.writeText(url).then(() => {
          if (this.successMsg) {
            this.successMsg.removeAttribute('hidden');
            setTimeout(() => this.successMsg.setAttribute('hidden', ''), 2000);
          }
        }).catch(() => {});
      }
    });
  }
}
customElements.define('share-button', ShareButton);

/* ============================================================
   SECTION 19 â€” CUSTOM ELEMENT: ShowMoreButton
   ============================================================ */

class ShowMoreButton extends HTMLElement {
  connectedCallback() {
    const btn = qs('button', this);
    if (!btn) return;
    btn.addEventListener('click', () => {
      const targetId = this.dataset.targetId;
      const target = targetId ? qs(`#${targetId}`) : null;
      if (target) {
        target.removeAttribute('hidden');
        this.setAttribute('hidden', '');
      }
    });
  }
}
if (!document.querySelector('script[src*="show-more.js"]') && !customElements.get('show-more-button')) {
  customElements.define('show-more-button', ShowMoreButton);
}

/* ============================================================
   SECTION 20 â€” CUSTOM ELEMENT: CopyButton
   ============================================================ */

class CopyButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () => {
      const text = this.dataset.copyText || '';
      navigator.clipboard && navigator.clipboard.writeText(text).catch(() => {});
      const success = qs('[data-success]', this);
      if (success) {
        success.removeAttribute('hidden');
        setTimeout(() => success.setAttribute('hidden', ''), 2000);
      }
    });
  }
}
customElements.define('copy-button', CopyButton);

/* ============================================================
   SECTION 21 â€” CUSTOM ELEMENT: ProductRecommendations
   ============================================================ */

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    if (!productId || !sectionId) return;

    const url = `/recommendations/products.json?product_id=${productId}&limit=4&section_id=${sectionId}`;
    safeFetch(url)
      .then(r => r.json())
      .then(data => {
        if (!data.products || data.products.length === 0) {
          this.setAttribute('hidden', '');
        }
      })
      .catch(() => this.setAttribute('hidden', ''));
  }
}
customElements.define('product-recommendations', ProductRecommendations);

/* ============================================================
   SECTION 22 â€” CUSTOM ELEMENT: PickupAvailability (stub)
   ============================================================ */

class PickupAvailability extends HTMLElement {
  connectedCallback() {}

  fetchAvailability(variantId) {
    if (!variantId) return;
    safeFetch(`/variants/${variantId}/?section_id=pickup-availability`)
      .then(r => r.text())
      .then(html => {
        const preview = qs('pickup-availability-preview', this);
        if (preview) preview.innerHTML = html;
        this.setAttribute('available', '');
      })
      .catch(() => {});
  }
}
customElements.define('pickup-availability', PickupAvailability);

/* ============================================================
   SECTION 23 â€” ANNOUNCEMENT BAR / PROMO POPUP (vanilla JS)
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {

  // Announcement bar close button
  const announcementClose = qs('.announcement-bar__close');
  const announcementBar = qs('.announcement-bar');
  if (announcementClose && announcementBar) {
    announcementClose.addEventListener('click', () => {
      announcementBar.style.display = 'none';
    });
  }

  // Generic accordion â€” <details> / <summary> pairs not in custom elements
  qsa('details.accordion').forEach(details => {
    const summary = qs('summary', details);
    summary && summary.addEventListener('click', e => {
      if (details.hasAttribute('open')) {
        e.preventDefault();
        details.removeAttribute('open');
      }
    });
  });

  // Image drag prevention (common Shrine feature)
  qsa('.product__media img').forEach(img => {
    img.addEventListener('dragstart', e => e.preventDefault());
  });

  // No-JS class removal
  document.documentElement.className = document.documentElement.className.replace('no-js', 'js');

  // Scroll-to-top button
  const scrollTopBtn = qs('.scroll-to-top-btn');
  if (scrollTopBtn) {
    window.addEventListener('scroll', debounce(() => {
      scrollTopBtn.classList.toggle('is-visible', window.scrollY > 400);
    }, 100));
    scrollTopBtn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // Header cart icon + any CartDrawer trigger -> open the drawer (not the /cart page)
  qsa('#cart-icon-bubble, [aria-controls="CartDrawer"]').forEach(trigger => {
    trigger.addEventListener('click', e => {
      const cartDrawer = qs('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        e.preventDefault();
        cartDrawer.open();
      }
    });
  });

  // Mobile header toggle (hamburger)
  const menuToggle = qs('[aria-controls="menu-drawer"]');
  const menuDrawer = qs('menu-drawer') || qs('header-drawer');
  if (menuToggle && menuDrawer) {
    menuToggle.addEventListener('click', () => {
      const isOpen = menuToggle.getAttribute('aria-expanded') === 'true';
      menuToggle.setAttribute('aria-expanded', !isOpen);
      if (!isOpen) {
        menuDrawer.openMenuDrawer && menuDrawer.openMenuDrawer(menuToggle);
      } else {
        menuDrawer.closeMenuDrawer && menuDrawer.closeMenuDrawer();
      }
    });
  }

  // Close all overlays on ESC
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    const cartDrawer = qs('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) cartDrawer.close();
    const menuDrawerEl = qs('menu-drawer') || qs('header-drawer');
    if (menuDrawerEl) menuDrawerEl.closeMenuDrawer && menuDrawerEl.closeMenuDrawer();
  });

});

/* ============================================================
   SECTION 24 â€” SHOPIFY THEME EDITOR COMPATIBILITY
   ============================================================ */

document.addEventListener('shopify:section:load', e => {
  // Re-run animations if applicable
  if (typeof initAnimations === 'function') initAnimations(e.target, true);
});

document.addEventListener('shopify:block:select', e => {
  const block = e.target;
  if (block.classList.contains('slideshow__slide')) {
    const slideshow = block.closest('slideshow-component');
    if (slideshow && slideshow.pause) slideshow.pause();
  }
});

document.addEventListener('shopify:block:deselect', e => {
  const block = e.target;
  const slideshow = block && block.closest('slideshow-component');
  if (slideshow && slideshow.play && slideshow.autoplaySpeed > 0) slideshow.play();
});

/* ============================================================
   SECTION 25 â€” LOCALIZATION FORM STUB
   ============================================================ */

class GGLocalizationForm extends HTMLElement {
  connectedCallback() {
    this.form = qs('form', this);
    this.inputEl = qs('input[name="locale_code"]', this) || qs('input[name="country_code"]', this);
    qsa('a[data-value]', this).forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault();
        if (this.inputEl) this.inputEl.value = link.dataset.value;
        this.form && this.form.submit();
      });
    });
  }
}
if (!customElements.get('localization-form')) {
  customElements.define('localization-form', GGLocalizationForm);
}


/* ============================================================
   PER-VARIANT DESCRIPTION
   A product description may contain one [data-blend="<variantId>"] block per
   variant. Show the block for the selected variant and hide the rest.

   Deliberately self-contained: it resolves the variant the same way the
   theme's own inline product script does (the JSON inside <variant-selects>)
   rather than depending on VariantSelects.currentVariant, which resolves to
   null on any product without a 3D model. Products with no [data-blend]
   blocks are untouched.
   ============================================================ */
(function () {
  function blendBlocks() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-blend]'));
  }

  function selectedVariant(root) {
    var script = root.querySelector('script[type="application/json"]');
    if (!script) return null;
    var data;
    try { data = JSON.parse(script.textContent); } catch (e) { return null; }
    var variants = Array.isArray(data) ? data : data.variants;
    if (!Array.isArray(variants)) return null;

    var groups = Array.prototype.slice.call(root.querySelectorAll('select, fieldset'));
    var chosen = groups.map(function (el) {
      if (el.tagName === 'SELECT') return el.value;
      var checked = el.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : '';
    }).filter(function (v) { return v !== ''; });
    if (!chosen.length) return null;

    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!Array.isArray(v.options)) continue;
      var match = true;
      for (var j = 0; j < chosen.length; j++) {
        if (v.options[j] !== chosen[j]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  function syncBlendDescription() {
    var blocks = blendBlocks();
    if (!blocks.length) return;
    var root = document.querySelector('variant-selects');
    if (!root) return;
    var variant = selectedVariant(root);
    if (!variant) return;
    var id = String(variant.id);
    if (!blocks.some(function (b) { return b.dataset.blend === id; })) return;
    blocks.forEach(function (b) { b.hidden = b.dataset.blend !== id; });
  }

  function bind() {
    if (!blendBlocks().length) return;
    var root = document.querySelector('variant-selects');
    if (!root) return;
    root.addEventListener('change', function () { setTimeout(syncBlendDescription, 0); });
    syncBlendDescription();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();


/* ============================================================
   UPSELL PER-VARIANT DESCRIPTION
   The upsell card already swaps its image and variant id when the blend
   dropdown changes, but .upsell__desc is a static block setting, so every
   blend showed the same "collagen, arnica, lavender..." line.

   This rewrites that line to match the selected blend. Keyed on a stable
   keyword in the option value rather than the full string or a variant id,
   so it survives a retitled variant. Any upsell whose option does not match
   is left exactly as the theme rendered it.
   ============================================================ */
(function () {
  var OIL_HANDLE = 'the-ryng-body-oil';

  var BLEND_COPY = [
    ['anti-cellulite', 'Collagen and stem cell infused. For thighs, hips and glutes.'],
    ['sore muscle',    'Arnica and peppermint. Cools on contact. For legs and shoulders.'],
    ['debloat',        'Lavender and chamomile. The evening one, for the waist and stomach.'],
    ['soft & smooth',  'Rosehip and ylang ylang. The richest of the five, for skin that feels rough.'],
    ['rosehip',        'Rosehip and ylang ylang. The richest of the five, for skin that feels rough.'],
    ['cramp',          'Pure magnesium chloride. For calves and feet at night.']
  ];

  function copyFor(value) {
    var v = String(value || '').toLowerCase();
    for (var i = 0; i < BLEND_COPY.length; i++) {
      if (v.indexOf(BLEND_COPY[i][0]) !== -1) return BLEND_COPY[i][1];
    }
    return null;
  }

  function syncCard(card) {
    if (!card || card.dataset.handle !== OIL_HANDLE) return;
    var select = card.querySelector('select.variant-dropdown');
    var desc = card.querySelector('.upsell__desc');
    if (!select || !desc) return;
    var text = copyFor(select.value);
    if (!text) return;
    if (!desc.dataset.defaultCopy) desc.dataset.defaultCopy = desc.textContent.trim();
    desc.textContent = text;
  }

  function cards() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.upsell[data-handle="' + OIL_HANDLE + '"]'));
  }

  function bind() {
    var list = cards();
    if (!list.length) return;
    list.forEach(function (card) {
      if (card.dataset.blendCopyBound === 'true') return;
      card.dataset.blendCopyBound = 'true';
      card.addEventListener('change', function (e) {
        if (e.target && e.target.classList.contains('variant-dropdown')) {
          setTimeout(function () { syncCard(card); }, 0);
        }
      });
      syncCard(card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
  // upsell cards can be injected after load, so re-bind when the DOM settles
  if (window.MutationObserver) {
    var mo = new MutationObserver(function () { bind(); });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
